import { assetStore } from '../assets/assetStore';
import { cloudUrl } from '../assets/assetCloud';

/** Parse a Blob's bytes as JSON (null on failure). FileReader rather than
 *  Blob.text(): jsdom's Blob is unreadable via text(). */
function blobToJson(blob: Blob): Promise<unknown | null> {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => {
      try {
        resolve(JSON.parse(String(fr.result)));
      } catch {
        resolve(null);
      }
    };
    fr.onerror = () => resolve(null);
    fr.readAsText(blob);
  });
}

/**
 * Parse a lottie asset ref's bytes as JSON. Two ref shapes reach this:
 *  - `asset://sha256-…` — the canonical content-hash ref; local IndexedDB store
 *    first, then the CDN (`cloudUrl`).
 *  - `blob:` / `http(s):` — the render seam's asset resolver (`rewriteAssetRefs`)
 *    rewrites `data-lottie="asset://…"` to a live objectURL/CDN url in the DOM
 *    the hook scans, so the hook often hands us the already-resolved URL. Those
 *    are directly fetchable handles to the exact bytes — fetch as-is.
 * Returns null when missing or unparsable so the hook surfaces one errorRing
 * entry instead of throwing.
 */
export async function loadAssetJson(ref: string): Promise<unknown | null> {
  let blob: Blob | null = null;
  if (/^(blob:|https?:)/.test(ref)) {
    blob = await fetch(ref)
      .then((r) => (r.ok ? r.blob() : null))
      .catch(() => null);
  } else {
    const id = ref.replace(/^asset:\/\//, '');
    blob = await assetStore.get(id);
    if (!blob) {
      blob = await fetch(cloudUrl(id))
        .then((r) => (r.ok ? r.blob() : null))
        .catch(() => null);
    }
  }
  if (!blob) return null;
  return blobToJson(blob);
}
